// Mockable provider transport — timeout, bounded retry/backoff, no secret logging.
import { directorError, type StructuredDirectorError } from '../prompts/errors.js';

export const DEFAULT_TIMEOUT_MS = 30_000;
export const DEFAULT_MAX_RETRIES = 2;
export const DEFAULT_BASE_BACKOFF_MS = 100;

export interface TransportOptions {
  timeoutMs?: number;
  maxRetries?: number;
  baseBackoffMs?: number;
  fetchImpl?: typeof fetch;
}

export class TransportFailure extends Error {
  constructor(message: string, opts?: { cause?: unknown }) {
    super(message, opts);
    this.name = 'TransportFailure';
  }
}

export async function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new TransportFailure(`Provider request timed out (${label})`)),
      ms,
    );
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export async function withRetry<T>(
  fn: (attempt: number) => Promise<T>,
  opts: TransportOptions = {},
): Promise<T> {
  const maxRetries = opts.maxRetries ?? DEFAULT_MAX_RETRIES;
  const baseBackoff = opts.baseBackoffMs ?? DEFAULT_BASE_BACKOFF_MS;
  let lastError: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn(attempt);
    } catch (e) {
      lastError = e;
      if (attempt >= maxRetries) break;
      await sleep(baseBackoff * 2 ** attempt);
    }
  }
  throw lastError instanceof Error ? lastError : new TransportFailure('Provider request failed');
}

export function mapTransportToDirectorError(err: unknown): StructuredDirectorError {
  const message =
    err instanceof TransportFailure
      ? err.message
      : err instanceof Error
        ? 'Provider request failed.'
        : 'Provider request failed.';
  return directorError('transport_error', message, {
    actions: ['retry', 'fallback'],
    details: err instanceof Error ? [err.name] : [],
  });
}

const SECRET_PATTERNS = [/sk-[a-zA-Z0-9]+/g, /AIza[a-zA-Z0-9_-]+/g];

/** Strip common key shapes before any diagnostic string is emitted. */
export function redactSecrets(text: string): string {
  return SECRET_PATTERNS.reduce((s, p) => s.replace(p, '[REDACTED]'), text);
}

/** Assert serialized payloads never carry forbidden samples (keys, raw bodies). */
export function assertNoSecretLeaks(payload: unknown, forbiddenSamples: string[]): void {
  const json = JSON.stringify(payload);
  for (const sample of forbiddenSamples) {
    if (sample.length > 0 && json.includes(sample)) {
      throw new Error('Secret or raw payload would leak into persistent record');
    }
  }
}
